import { ChevronDown, ChevronRight, Loader2, Send } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTable,
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableToolbar,
  DataTableTr,
} from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useAdminQuestionnaireTemplates } from '@/features/admin-questionnaire-templates/api';
import { useAdminUsers } from '@/features/admin-users/api';
import {
  resolvedNameLabel,
  useResolveUserDisplayNames,
} from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

import { useBulkSendJobs } from '../api';
import { BulkSendCreateDialog } from './BulkSendCreateDialog';
import { BulkSendJobDetails } from './BulkSendJobDetails';

const EMPTY = '—';
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const JOB_ID_DEBOUNCE_MS = 300;
const LAUNCH_POLL_GRACE_MS = 30_000;
const CREATOR_OPTIONS_LIMIT = 100;
const COLSPAN = 7;
const STATUS_OPTIONS = ['STARTING', 'STARTED', 'COMPLETED', 'FAILED', 'STOPPED', 'ABANDONED'] as const;

function statusVariant(status: string): 'secondary' | 'destructive' | 'outline' {
  if (status === 'FAILED' || status === 'STOPPED' || status === 'ABANDONED') return 'destructive';
  if (status === 'COMPLETED') return 'secondary';
  return 'outline';
}

function formatWhen(iso: string | null): string {
  if (iso === null) return EMPTY;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? EMPTY : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

/** The "Bulk Send" tab: launch a job and browse every bulk-send run in one filterable, expandable table. */
export function BulkSendPanel({
  initialJobId,
  autoRefresh = false,
}: {
  readonly initialJobId?: string | undefined;
  readonly autoRefresh?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState<number>(DEFAULT_PAGE_SIZE);
  // A `?job=<id>` deep link pre-filters to (and expands) that job.
  const [jobIdInput, setJobIdInput] = useState(initialJobId ?? '');
  const [createdBy, setCreatedBy] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(initialJobId !== undefined && initialJobId.length > 0 ? [initialJobId] : []),
  );
  // Epoch ms until which to keep polling after a launch (the new job's snapshot lands a beat later).
  const [pollUntil, setPollUntil] = useState(0);
  // Rows whose status/delivery-counts changed since the last poll — flagged with a dot until expanded.
  const [updatedIds, setUpdatedIds] = useState<ReadonlySet<string>>(new Set());
  const debouncedJobId = useDebouncedValue(jobIdInput, JOB_ID_DEBOUNCE_MS);

  function toggleExpanded(id: string): void {
    // Opening a row means the user has seen its latest data — clear its "updated" flag.
    setUpdatedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function resetToFirstPage(): void {
    setPage(0);
  }

  const templatesQuery = useAdminQuestionnaireTemplates();
  const templates = useMemo(
    () => (templatesQuery.data ?? []).filter((tmpl) => tmpl.active),
    [templatesQuery.data],
  );

  const usersQuery = useAdminUsers({ page: 0, size: CREATOR_OPTIONS_LIMIT });
  const creatorOptions = useMemo(
    () =>
      (usersQuery.data?.rows ?? []).map((u) => ({
        value: u.uuid,
        label: u.fullName.length > 0 ? u.fullName : u.name,
      })),
    [usersQuery.data],
  );

  const jobsQuery = useBulkSendJobs(
    page,
    size,
    {
      jobId: debouncedJobId,
      createdBy,
      templateId,
      status,
    },
    pollUntil,
    autoRefresh,
  );

  const rows = useMemo(() => jobsQuery.data?.rows ?? [], [jobsQuery.data]);
  const totalElements = jobsQuery.data?.totalElements ?? 0;

  // Flag rows whose signature (status + end time + delivery counts) changed vs the previous poll, so
  // the user knows to expand and see the new data. First sighting of a row never counts as an update.
  const prevSignatures = useRef<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    const previous = prevSignatures.current;
    const next = new Map<string, string>();
    const changed: string[] = [];
    for (const job of rows) {
      const sig = `${job.status}|${job.endTime ?? ''}|${JSON.stringify(job.counts)}`;
      next.set(job.jobId, sig);
      const before = previous.get(job.jobId);
      if (before !== undefined && before !== sig) changed.push(job.jobId);
    }
    prevSignatures.current = next;
    if (changed.length > 0) {
      setUpdatedIds((cur) => {
        const s = new Set(cur);
        changed.forEach((id) => s.add(id));
        return s;
      });
    }
  }, [rows]);

  const creatorNames = useResolveUserDisplayNames(rows.map((row) => row.createdBy ?? ''));
  // No creator or lookup in flight → "—"; lookup failed → "Unknown user".
  function creatorLabel(guid: string | null): string {
    if (guid === null || guid.length === 0) return EMPTY;
    return resolvedNameLabel(creatorNames.get(guid), t('patient.careTeam.unknownUser')) ?? EMPTY;
  }

  const from = totalElements === 0 ? 0 : page * size + 1;
  const to = page * size + rows.length;

  return (
    <div className="px-8 py-5 space-y-4 bg-background">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          type="search"
          className="h-9 w-32 text-sm"
          placeholder={t('routes.questionnaires.bulkSend.filters.jobId')}
          value={jobIdInput}
          onChange={(e) => {
            setJobIdInput(e.target.value);
            resetToFirstPage();
          }}
          aria-label={t('routes.questionnaires.bulkSend.filters.jobId')}
        />
        <Select
          value={templateId ?? 'all'}
          onChange={(e) => {
            setTemplateId(e.target.value === 'all' ? null : e.target.value);
            resetToFirstPage();
          }}
          className="h-9 w-56 text-sm"
          aria-label={t('routes.questionnaires.bulkSend.filters.template')}
        >
          <option value="all">{t('routes.questionnaires.bulkSend.filters.allTemplates')}</option>
          {templates.map((tmpl) => (
            <option key={tmpl.id} value={tmpl.id}>
              {tmpl.label}
            </option>
          ))}
        </Select>
        <Select
          value={createdBy ?? 'all'}
          onChange={(e) => {
            setCreatedBy(e.target.value === 'all' ? null : e.target.value);
            resetToFirstPage();
          }}
          className="h-9 w-48 text-sm"
          aria-label={t('routes.questionnaires.bulkSend.filters.createdBy')}
        >
          <option value="all">{t('routes.questionnaires.bulkSend.filters.allCreators')}</option>
          {creatorOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Select
          value={status ?? 'all'}
          onChange={(e) => {
            setStatus(e.target.value === 'all' ? null : e.target.value);
            resetToFirstPage();
          }}
          className="h-9 w-40 text-sm"
          aria-label={t('routes.questionnaires.bulkSend.filters.status')}
        >
          <option value="all">{t('routes.questionnaires.bulkSend.filters.allStatuses')}</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <Button
          type="button"
          className="ml-auto"
          onClick={() => {
            setDialogOpen(true);
          }}
        >
          <Send className="me-2 h-4 w-4" aria-hidden="true" />
          {t('routes.questionnaires.bulkSend.createButton')}
        </Button>
      </div>

      {jobsQuery.isError && (
        <div className="max-w-xl">
          <FetchErrorCallout
            error={jobsQuery.error}
            heading={t('routes.questionnaires.bulkSend.error.heading')}
            message={t('routes.questionnaires.bulkSend.error.message')}
            codeLabel={t('routes.questionnaires.bulkSend.error.codeLabel')}
            onRetry={() => {
              jobsQuery.refetch().catch(() => undefined);
            }}
            retryLabel={t('routes.questionnaires.bulkSend.error.retry')}
          />
        </div>
      )}

      {jobsQuery.isSuccess && rows.length === 0 && (
        <p className="py-12 text-center text-sm text-muted-foreground" role="status">
          {t('routes.questionnaires.bulkSend.empty')}
        </p>
      )}

      {rows.length > 0 && (
        <DataTable aria-label={t('routes.questionnaires.bulkSend.tableHeading')}>
          <DataTableToolbar
            pageSize={size}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={(next) => {
              setSize(next);
              resetToFirstPage();
            }}
            from={from}
            to={to}
            total={totalElements}
            onPagePrevious={() => {
              setPage((p) => Math.max(0, p - 1));
            }}
            onPageNext={() => {
              setPage((p) => p + 1);
            }}
            canGoPrevious={page > 0}
            canGoNext={to < totalElements}
            pageSizeSelectId="bulk-send-jobs-page-size"
          />
          <DataTableTable aria-label={t('routes.questionnaires.bulkSend.tableHeading')}>
            <DataTableThead>
              <DataTableHeaderRow>
                <DataTableTh widthClassName="w-24">{t('routes.questionnaires.bulkSend.table.jobId')}</DataTableTh>
                <DataTableTh widthClassName="w-[22%]">
                  {t('routes.questionnaires.bulkSend.table.template')}
                </DataTableTh>
                <DataTableTh widthClassName="w-40">
                  {t('routes.questionnaires.bulkSend.table.createdBy')}
                </DataTableTh>
                <DataTableTh widthClassName="w-44">
                  {t('routes.questionnaires.bulkSend.table.started')}
                </DataTableTh>
                <DataTableTh widthClassName="w-44">{t('routes.questionnaires.bulkSend.table.ended')}</DataTableTh>
                <DataTableTh widthClassName="w-32">{t('routes.questionnaires.bulkSend.table.status')}</DataTableTh>
                <DataTableTh widthClassName="w-24" align="right">
                  {t('routes.questionnaires.bulkSend.table.delivered')}
                </DataTableTh>
              </DataTableHeaderRow>
            </DataTableThead>
            <DataTableTbody>
              {rows.map((job) => {
                const isOpen = expanded.has(job.jobId);
                const isUpdated = updatedIds.has(job.jobId) && !isOpen;
                const total = Object.values(job.counts).reduce((sum, n) => sum + n, 0);
                const delivered = job.counts.DELIVERED ?? 0;
                return (
                  <Fragment key={job.jobId}>
                    <DataTableTr
                      className="cursor-pointer"
                      onClick={() => {
                        toggleExpanded(job.jobId);
                      }}
                    >
                      <DataTableTd className="pr-1 tabular-nums font-medium text-foreground" dataOffset={false}>
                        <span className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpanded(job.jobId);
                            }}
                            aria-expanded={isOpen}
                            aria-label={t('routes.questionnaires.bulkSend.table.toggle')}
                            className="shrink-0 text-muted-foreground hover:text-foreground"
                          >
                            {isOpen ? (
                              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                            )}
                          </button>
                          #{job.jobId}
                          {isUpdated && (
                            <span
                              className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                              role="status"
                              aria-label={t('routes.questionnaires.bulkSend.table.updated')}
                              title={t('routes.questionnaires.bulkSend.table.updated')}
                            />
                          )}
                        </span>
                      </DataTableTd>
                      <DataTableTd className="truncate text-foreground/90" dataOffset>
                        {job.templateName ?? (job.templateId.length > 0 ? job.templateId : EMPTY)}
                      </DataTableTd>
                      <DataTableTd className="truncate text-foreground/90" dataOffset>
                        {creatorLabel(job.createdBy)}
                      </DataTableTd>
                      <DataTableTd className="whitespace-nowrap text-foreground/90" dataOffset>
                        {formatWhen(job.startTime)}
                      </DataTableTd>
                      <DataTableTd className="whitespace-nowrap text-foreground/90" dataOffset>
                        {formatWhen(job.endTime)}
                      </DataTableTd>
                      <DataTableTd dataOffset>
                        <Badge variant={statusVariant(job.status)} className="whitespace-nowrap">
                          {!job.isTerminal && <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />}
                          {job.status}
                        </Badge>
                      </DataTableTd>
                      <DataTableTd className="tabular-nums text-foreground/90" align="right" dataOffset>
                        {total > 0 ? `${String(delivered)}/${String(total)}` : EMPTY}
                      </DataTableTd>
                    </DataTableTr>
                    {isOpen && (
                      <DataTableTr>
                        <DataTableTd colSpan={COLSPAN} className="bg-muted/30">
                          <div className="pl-5">
                            <BulkSendJobDetails
                              job={job}
                              creatorName={creatorLabel(job.createdBy)}
                              autoRefresh={autoRefresh}
                            />
                          </div>
                        </DataTableTd>
                      </DataTableTr>
                    )}
                  </Fragment>
                );
              })}
            </DataTableTbody>
          </DataTableTable>
        </DataTable>
      )}

      <BulkSendCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onLaunched={() => {
          // The job's snapshot is written asynchronously by its first step, so it may not be visible
          // on an immediate refetch. Poll for a grace window until it appears.
          setPollUntil(Date.now() + LAUNCH_POLL_GRACE_MS);
          jobsQuery.refetch().catch(() => undefined);
        }}
      />
    </div>
  );
}
